import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  Trash2,
  Plus,
  Loader2,
  Sparkles,
  Eye,
  BarChart3,
  Clock,
  DollarSign,
  CalendarRange,
  Download,
  FileJson,
  FileText,
  Webhook,
  RefreshCw,
  Copy,
  Check,
  EyeOff,
  Plug,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import {
  Select as ShSelect,
  SelectContent as ShSelectContent,
  SelectItem as ShSelectItem,
  SelectTrigger as ShSelectTrigger,
  SelectValue as ShSelectValue,
} from "@/components/ui/select";
import {
  getAgent,
  updateAgent,
  agentJsonUrl,
  agentMarkdownUrl,
  agentPdfUrl,
  getWebhookInfo,
  rotateWebhookSecret,
  adminListPlans,
  adminAssignPlan,
  llmProviders,
  agentLlmGet,
  agentLlmSave,
  agentLlmDelete,
  agentLlmCheck,
  agentKbl,
} from "@/lib/api";
import InviktusLogo from "@/components/InviktusLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import LlmKeysPanel from "@/components/LlmKeysPanel";

export default function AgentEdit() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [wh, setWh] = useState(null);
  const [whLoading, setWhLoading] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState("");
  const [plans, setPlans] = useState([]);
  const [planAssigning, setPlanAssigning] = useState(false);

  // ---- LLM (BYO API key) state ----
  const [llmProvidersList, setLlmProvidersList] = useState([]);
  const [llmInfo, setLlmInfo] = useState(null);

  useEffect(() => {
    llmProviders().then((d) => setLlmProvidersList(d.providers || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!slug) return;
    agentLlmGet(slug).then(setLlmInfo).catch(() => setLlmInfo(null));
  }, [slug]);

  const reloadLlm = async () => {
    try {
      const d = await agentLlmGet(slug);
      setLlmInfo(d);
    } catch {
      setLlmInfo(null);
    }
  };

  // ---- Per-agent Knowledge Library ----
  const [kbl, setKbl] = useState(null);
  useEffect(() => {
    if (!slug) return;
    agentKbl(slug).then(setKbl).catch(() => setKbl(null));
  }, [slug]);

  const onLlmSave = async ({ provider, api_key, model }) => {
    await agentLlmSave(slug, { provider, api_key, model });
    await reloadLlm();
  };
  const onLlmDelete = async () => {
    await agentLlmDelete(slug);
    await reloadLlm();
  };
  const onLlmCheck = async ({ api_key, model }) => {
    const r = await agentLlmCheck(slug, { api_key, model });
    await reloadLlm();
    return r;
  };

  useEffect(() => {
    adminListPlans().then((d) => setPlans(d.plans || [])).catch(() => {});
  }, []);

  const onAssignPlan = async (planId) => {
    try {
      setPlanAssigning(true);
      const { agent: a2 } = await adminAssignPlan(slug, planId === "__none__" ? null : planId);
      setAgent(a2);
      toast.success(planId === "__none__" ? "Asignación retirada." : "Plan asignado.");
    } catch {
      toast.error("Error al asignar plan.");
    } finally {
      setPlanAssigning(false);
    }
  };

  const loadWebhook = async () => {
    try {
      setWhLoading(true);
      const info = await getWebhookInfo(slug);
      setWh(info);
    } catch {
      toast.error("No se pudo cargar el webhook.");
    } finally {
      setWhLoading(false);
    }
  };

  const onRotate = async () => {
    if (!window.confirm("¿Rotar el secreto del webhook? Las integraciones existentes dejarán de funcionar hasta actualizarse.")) return;
    try {
      await rotateWebhookSecret(slug);
      await loadWebhook();
      toast.success("Secreto rotado.");
    } catch {
      toast.error("No se pudo rotar.");
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
    } catch {
      toast.error("No se pudo copiar.");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { agent: a } = await getAgent(slug);
        setAgent(a);
        loadWebhook();
      } catch {
        toast.error("Agente no encontrado.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const onSave = async () => {
    if (!agent) return;
    setSaving(true);
    try {
      await updateAgent(slug, {
        name: agent.name,
        description: agent.description,
        system_instruction: agent.system_instruction,
        prompts: agent.prompts,
        tools: agent.tools,
        metrics_config: agent.metrics_config,
      });
      toast.success("Agente guardado.");
    } catch {
      toast.error("Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (k, v) => setAgent((a) => ({ ...a, [k]: v }));
  const updatePrompt = (i, k, v) =>
    setAgent((a) => {
      const next = [...(a.prompts || [])];
      next[i] = { ...next[i], [k]: v };
      return { ...a, prompts: next };
    });
  const removePrompt = (i) =>
    setAgent((a) => {
      const next = [...(a.prompts || [])];
      next.splice(i, 1);
      return { ...a, prompts: next };
    });
  const addPrompt = () =>
    setAgent((a) => ({
      ...a,
      prompts: [
        ...(a.prompts || []),
        { id: Math.random().toString(36).slice(2, 10), title: "", prompt_text: "", activity_ref: "" },
      ],
    }));
  const toggleTool = (id) =>
    setAgent((a) => ({
      ...a,
      tools: (a.tools || []).map((t) => (t.id === id ? { ...t, enabled: !t.enabled } : t)),
    }));

  const updateMetricsCfg = (k, v) =>
    setAgent((a) => ({
      ...a,
      metrics_config: { ...(a.metrics_config || {}), [k]: v },
    }));

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 text-slate-700">
        <Loader2 className="mr-3 h-5 w-5 animate-spin" />
        <span className="text-sm uppercase tracking-[0.22em]">Cargando...</span>
      </div>
    );
  }
  if (!agent) return null;

  const charCount = (agent.system_instruction || "").length;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="agent-edit-page">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4 lg:px-10">
          <div className="flex items-center gap-5">
            <button
              onClick={() => navigate(`/agentes/${slug}`)}
              className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-slate-500 hover:text-slate-900"
              data-testid="back-to-agent-btn"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Vista del agente
            </button>
            <Separator orientation="vertical" className="hidden h-6 bg-slate-300 sm:block" />
            <InviktusLogo className="text-slate-900 hidden sm:inline-flex" size={22} />
            <ThemeToggle />
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate(`/agentes/${slug}/mcps`)}
              className="rounded-sm border-accent-blue/40 text-xs uppercase tracking-[0.2em] text-accent-blue hover:bg-accent-blue/5"
              data-testid="agent-mcps-btn"
            >
              <Plug className="mr-2 h-4 w-4" /> MCPs requeridos
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/agentes/${slug}`)}
              className="rounded-sm border-slate-300 text-xs uppercase tracking-[0.2em] text-slate-700 hover:bg-slate-100"
            >
              <Eye className="mr-2 h-4 w-4" /> Previsualizar
            </Button>
            <Button
              onClick={onSave}
              disabled={saving}
              className="rounded-sm bg-slate-900 text-xs uppercase tracking-[0.2em] text-white hover:bg-slate-800"
              data-testid="save-agent-btn"
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Guardar
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-12 lg:px-10">
        {/* Title */}
        <div className="border-b border-slate-300 pb-8">
          <p className="eyebrow text-accent-blue">Edición de Agente IA</p>
          <h1 className="mt-4 font-display text-4xl font-medium leading-tight text-slate-900">
            {agent.name}
          </h1>
          <p className="mt-2 text-xs uppercase tracking-[0.18em] text-slate-500">
            Departamento · <span className="text-slate-800">{agent.department}</span> · slug:{" "}
            <span className="font-mono normal-case text-slate-700">{slug}</span>
          </p>
        </div>

        {/* Name + Description */}
        <Section title="Identidad">
          <Field label="Nombre" required>
            <Input
              value={agent.name || ""}
              onChange={(e) => updateField("name", e.target.value)}
              className="rounded-sm border-slate-300"
              data-testid="agent-name-input"
            />
          </Field>
          <Field
            label="Descripción"
            hint="Describa lo que el agente de IA hace para el usuario. Esto es sólo para la información del usuario y NO se incluirá en la instrucción del sistema."
          >
            <Textarea
              value={agent.description || ""}
              onChange={(e) => updateField("description", e.target.value)}
              rows={4}
              className="rounded-sm border-slate-300"
              data-testid="agent-description-input"
            />
          </Field>
        </Section>

        {/* System Instruction */}
        <Section
          title="Instrucción del sistema"
          counter={`${charCount} caracteres`}
          warning="Sus usuarios no verán estas instrucciones."
        >
          <Textarea
            value={agent.system_instruction || ""}
            onChange={(e) => updateField("system_instruction", e.target.value)}
            rows={9}
            className="rounded-sm border-slate-300 font-mono text-[13px]"
            data-testid="agent-instruction-input"
          />
        </Section>

        {/* Regeneration timeline */}
        {Array.isArray(agent.regenerations) && agent.regenerations.length > 0 && (
          <Section
            title="Historial de regeneraciones"
            counter={`${agent.regenerations.length}`}
            hint="Cada vez que se ejecuta «Regenerar desde informe» se registra una entrada con la foto del flujo ABC inyectado en ese momento. Útil para auditar cambios."
          >
            <div className="overflow-x-auto" data-testid="agent-regen-timeline">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    <th className="px-3 py-2 text-left">Fecha</th>
                    <th className="px-3 py-2 text-right">Actividades</th>
                    <th className="px-3 py-2 text-right">Micro-tareas</th>
                    <th className="px-3 py-2 text-right">Herramientas</th>
                    <th className="px-3 py-2 text-right">Contexto ABC (chars)</th>
                  </tr>
                </thead>
                <tbody>
                  {[...agent.regenerations].reverse().map((r, i) => {
                    const idx = agent.regenerations.length - i;
                    return (
                      <tr
                        key={r.at || i}
                        className={i === 0 ? "border-b border-slate-200 bg-teal-50/40" : "border-b border-slate-100"}
                      >
                        <td className="px-3 py-2 align-top">
                          <span className="font-mono text-[11px] text-slate-500">v{idx}</span>{" "}
                          <span className="text-slate-700">
                            {r.at ? new Date(r.at).toLocaleString("es-419") : "—"}
                          </span>
                          {i === 0 && (
                            <span className="ml-2 inline-block border border-teal-700/40 bg-teal-50 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] text-teal-800">
                              actual
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-[12px] text-slate-700">{r.n_activities ?? 0}</td>
                        <td className="px-3 py-2 text-right font-mono text-[12px] text-slate-700">{r.n_micro_tasks ?? 0}</td>
                        <td className="px-3 py-2 text-right font-mono text-[12px] text-slate-700">{r.n_tools ?? 0}</td>
                        <td className="px-3 py-2 text-right font-mono text-[12px] text-slate-700">{(r.abc_flow_chars ?? 0).toLocaleString("es-419")}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* Conversation Starters */}
        <Section
          title="Iniciadores de conversación"
          counter={`${(agent.prompts || []).length}`}
          hint="Sugiera primeros mensajes para que el usuario seleccione al principio de la conversación. Cada uno corresponde a una actividad del análisis ABC."
        >
          <div className="space-y-4">
            {(agent.prompts || []).map((p, i) => (
              <div
                key={p.id || i}
                className="border border-slate-200 bg-white p-5"
                data-testid={`prompt-row-${i}`}
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
                  <div className="md:col-span-7">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Título (botón)
                    </label>
                    <Input
                      value={p.title || ""}
                      onChange={(e) => updatePrompt(i, "title", e.target.value)}
                      className="mt-2 rounded-sm border-slate-300"
                      placeholder="Ej.: Análisis de penetración último mes"
                    />
                  </div>
                  <div className="md:col-span-5">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Actividad ABC de origen
                    </label>
                    <Input
                      value={p.activity_ref || ""}
                      onChange={(e) => updatePrompt(i, "activity_ref", e.target.value)}
                      className="mt-2 rounded-sm border-slate-300"
                      placeholder="(opcional)"
                    />
                  </div>
                  <div className="md:col-span-12">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Prompt completo (texto que se envía al modelo)
                    </label>
                    <Textarea
                      value={p.prompt_text || ""}
                      onChange={(e) => updatePrompt(i, "prompt_text", e.target.value)}
                      rows={4}
                      className="mt-2 rounded-sm border-slate-300 font-mono text-[13px]"
                      placeholder="Orden completa que ejecutará el agente cuando el usuario haga clic en este botón."
                    />
                  </div>
                </div>
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={() => removePrompt(i)}
                    className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.18em] text-rose-600 hover:text-rose-700"
                    data-testid={`remove-prompt-${i}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Eliminar
                  </button>
                </div>
              </div>
            ))}
            <Button
              onClick={addPrompt}
              variant="outline"
              className="rounded-sm border-dashed border-slate-400 text-xs uppercase tracking-[0.2em] text-slate-700 hover:bg-slate-100"
              data-testid="add-prompt-btn"
            >
              <Plus className="mr-2 h-4 w-4" /> Añadir iniciador
            </Button>
          </div>
        </Section>

        {/* Tools / MCP */}
        <Section
          title="Herramientas (MCP)"
          warning="MCP marcado como MOCKED en este prototipo: el agente menciona el uso de la herramienta pero la conexión real se habilitará en producción."
          hint="Habilite las herramientas a las que este agente tendrá acceso. Las marcadas se mencionarán en su instrucción del sistema."
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(agent.tools || []).map((t) => (
              <label
                key={t.id}
                className={`flex items-center justify-between border bg-white p-4 transition ${
                  t.enabled ? "border-teal-600/60" : "border-slate-200"
                }`}
                data-testid={`tool-toggle-${t.id}`}
              >
                <div>
                  <p className="font-display text-base font-medium text-slate-900">{t.label}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    {t.mcp}
                  </p>
                </div>
                <Switch checked={!!t.enabled} onCheckedChange={() => toggleTool(t.id)} />
              </label>
            ))}
          </div>
        </Section>

        {/* Per-agent Knowledge Library */}
        <Section
          title="Knowledge Library del agente"
          counter={kbl?.documents?.length ?? "—"}
          hint="Documentos vinculados a este agente (filtrados por su empresa y departamento). Cada respuesta del agente se enriquece automáticamente con los fragmentos más relevantes vía RAG. Para subir o eliminar documentos use /admin/kbl."
        >
          <div className="border border-slate-200 bg-slate-50/60 px-4 py-3 text-xs text-slate-700" data-testid="agent-kbl-summary">
            <span className="font-mono uppercase tracking-[0.16em] text-slate-500">Scope:</span>{" "}
            <span className="font-semibold text-ink">
              {kbl?.company || "—"} / {kbl?.department || "—"}
            </span>{" "}
            ·{" "}
            <span className="font-mono">{kbl?.documents?.length ?? 0}</span> documento(s) ·{" "}
            <span className="font-mono">{kbl?.total_chunks ?? 0}</span> fragmento(s) RAG indexados.
          </div>
          {kbl?.documents?.length > 0 ? (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    <th className="px-3 py-2 text-left">Archivo</th>
                    <th className="px-3 py-2 text-right">Tamaño</th>
                    <th className="px-3 py-2 text-right">Caracteres</th>
                    <th className="px-3 py-2 text-right">RAG</th>
                    <th className="px-3 py-2 text-right">Cargado</th>
                  </tr>
                </thead>
                <tbody>
                  {kbl.documents.map((d) => (
                    <tr key={d.id} className="border-b border-slate-100" data-testid={`agent-kbl-doc-${d.id}`}>
                      <td className="px-3 py-2 align-top">
                        <span className="font-medium text-ink">{d.filename}</span>
                        {d.auto_source === "abc_report" && (
                          <span className="ml-2 inline-block border border-accent-blue/40 bg-accent-blue/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] text-accent-blue">
                            auto · informe
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[11px] text-slate-600">
                        {((d.size_bytes ?? 0) / 1024).toFixed(1)} KB
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[11px] text-slate-600">
                        {(d.chars ?? 0).toLocaleString("es-419")}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {d.rag_chunks ? (
                          <span className="font-mono text-[11px] text-accent-blue">
                            {d.rag_chunks} frag.
                          </span>
                        ) : (
                          <span className="font-mono text-[10px] text-slate-400">
                            sin indexar
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[10px] text-slate-500">
                        {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString("es-419") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-4 text-sm text-slate-500">
              Este agente todavía no tiene documentos en su Knowledge Library. Suba documentos
              específicos del departamento desde{" "}
              <a href="/admin/kbl" className="text-accent-blue hover:underline">/admin/kbl</a>{" "}
              filtrando por empresa <strong>{kbl?.company}</strong> y departamento{" "}
              <strong>{kbl?.department}</strong>.
            </p>
          )}
          {agent.industry_label && (
            <p className="mt-4 inline-flex items-center gap-2 border border-accent-blue/30 bg-accent-blue/5 px-3 py-1.5 text-[11px] text-accent-blue">
              <span>⚡</span>
              <span>
                Conocimiento experto de industria heredado:{" "}
                <strong>{agent.industry_label}</strong>
                {agent.industry_hint_chars
                  ? ` · ${(agent.industry_hint_chars).toLocaleString("es-419")} chars`
                  : ""}
              </span>
            </p>
          )}
        </Section>

        {/* LLM (BYO API key) per-agent override */}
        <Section
          title="Modelo LLM del agente"
          hint="Por defecto este agente usa el modelo configurado globalmente en Administración → Modelos LLM (o la clave universal de Inviktus si no hay default). Aquí puede asignar un proveedor + clave específicos para este agente, que tomarán prioridad."
        >
          <div className="border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-700" data-testid="agent-llm-effective">
            <span className="font-mono uppercase tracking-[0.16em] text-slate-500">Modelo activo:</span>{" "}
            <span className="font-semibold text-slate-900">
              {llmInfo?.effective?.provider || "—"}
              {llmInfo?.effective?.model ? ` · ${llmInfo.effective.model}` : ""}
            </span>{" "}
            <span className="ml-2 inline-flex items-center border border-slate-300 bg-white px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-slate-600">
              fuente: {llmInfo?.effective?.source || "—"}
            </span>
          </div>
          <div className="mt-6">
            <LlmKeysPanel
              providers={
                llmInfo?.override?.provider
                  ? llmProvidersList.filter((p) => p.id === llmInfo.override.provider)
                  : llmProvidersList
              }
              rows={
                llmInfo?.override
                  ? [{ ...llmInfo.override, provider: llmInfo.override.provider }]
                  : []
              }
              onSave={onLlmSave}
              onDelete={onLlmDelete}
              onCheck={onLlmCheck}
              mode="agent"
              title="Override de proveedor"
              subtitle={
                llmInfo?.override
                  ? "Este agente ya tiene un modelo específico configurado. Para volver al default global, elimine la clave."
                  : "Seleccione un proveedor para asignar una clave específica para este agente."
              }
            />
          </div>
        </Section>

        {/* Metrics configuration */}
        <Section
          title="Métricas y verificación"
          hint="Configure cómo se calculan las métricas de impacto de este agente. La ventana se aplica al dashboard y al detalle de métricas."
        >
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 flex items-center gap-1.5">
                  <CalendarRange className="h-3 w-3" /> Ventana de verificación
                </p>
                <span className="font-mono text-sm font-semibold text-slate-900" data-testid="window-months-value">
                  {agent.metrics_config?.metrics_window_months ?? 1} {(agent.metrics_config?.metrics_window_months ?? 1) === 1 ? "mes" : "meses"}
                </span>
              </div>
              <Slider
                value={[agent.metrics_config?.metrics_window_months ?? 1]}
                min={1}
                max={12}
                step={1}
                onValueChange={(v) => updateMetricsCfg("metrics_window_months", v[0])}
                className="mt-5"
                data-testid="window-months-slider"
              />
              <div className="mt-2 flex justify-between text-[10px] uppercase tracking-[0.18em] text-slate-400">
                <span>1 mes</span>
                <span>12 meses</span>
              </div>
            </div>

            <div className="border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 flex items-center gap-1.5">
                  <Clock className="h-3 w-3" /> Minutos ahorrados / interacción
                </p>
                <span className="font-mono text-sm font-semibold text-slate-900" data-testid="minutes-per-int-value">
                  {agent.metrics_config?.minutes_per_interaction ?? 15} min
                </span>
              </div>
              <Slider
                value={[agent.metrics_config?.minutes_per_interaction ?? 15]}
                min={1}
                max={120}
                step={1}
                onValueChange={(v) => updateMetricsCfg("minutes_per_interaction", v[0])}
                className="mt-5"
                data-testid="minutes-per-int-slider"
              />
              <p className="mt-3 text-[11px] leading-snug text-slate-500">
                Tiempo promedio que cada empleado ahorra cuando el agente ejecuta una tarea.
              </p>
            </div>

            <div className="border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 flex items-center gap-1.5">
                  <DollarSign className="h-3 w-3" /> Tarifa USD / hora
                </p>
                <span className="font-mono text-sm font-semibold text-slate-900" data-testid="hourly-rate-value">
                  ${agent.metrics_config?.hourly_rate_usd ?? 25}
                </span>
              </div>
              <Input
                type="number"
                min={1}
                max={500}
                value={agent.metrics_config?.hourly_rate_usd ?? 25}
                onChange={(e) => updateMetricsCfg("hourly_rate_usd", Number(e.target.value) || 0)}
                className="mt-5 rounded-sm border-slate-300 font-mono"
                data-testid="hourly-rate-input"
              />
              <p className="mt-3 text-[11px] leading-snug text-slate-500">
                Coste por hora del colaborador del departamento (referencia LATAM).
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3 border border-dashed border-slate-300 bg-slate-50 px-5 py-3">
            <BarChart3 className="h-4 w-4 text-slate-500" />
            <p className="text-xs text-slate-600">
              Vea las métricas en tiempo real:
            </p>
            <a
              href={`/agentes/${slug}/metricas`}
              className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-blue hover:text-teal-800"
              data-testid="view-metrics-link"
            >
              Abrir métricas →
            </a>
            <a
              href={`/agentes/${slug}/pruebas`}
              className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-blue hover:text-teal-800"
              data-testid="open-agent-qa-link"
            >
              Pruebas de control →
            </a>
          </div>
        </Section>

        {/* Export / Portability */}
        <Section
          title="Exportar configuración"
          hint="Descargue la descripción, instrucción y prompts de este agente para re-implementarlo en otra plataforma (ChatGPT, Claude, Gemini, plataforma propia, etc.). El JSON sigue el esquema portable inviktus-abc-agent/1.0."
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="agent-exports-grid">
            <ExportCard
              href={agentJsonUrl(slug)}
              testId="export-agent-json"
              icon={FileJson}
              title="JSON"
              desc="Configuración estructurada para importar en otra herramienta."
              ext=".json"
            />
            <ExportCard
              href={agentMarkdownUrl(slug)}
              testId="export-agent-markdown"
              icon={FileText}
              title="Markdown"
              desc="Documento legible, listo para pegar en cualquier LLM."
              ext=".md"
            />
            <ExportCard
              href={agentPdfUrl(slug)}
              testId="export-agent-pdf"
              icon={Download}
              title="PDF"
              desc="Documento branded para compartir con el cliente."
              ext=".pdf"
            />
          </div>
        </Section>

        {/* Plan de facturación */}
        <Section
          title="Plan de facturación"
          hint="Asocie este agente a un plan parametrizado. La facturación se calcula automáticamente sobre eventos reales del webhook y/o uso de chat."
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="plan-assign-section">
            <div className="sm:col-span-2">
              <ShSelect
                value={agent.plan_id || "__none__"}
                onValueChange={onAssignPlan}
                disabled={planAssigning}
              >
                <ShSelectTrigger className="h-11 rounded-sm border-slate-300 bg-white text-sm" data-testid="agent-plan-select">
                  <ShSelectValue placeholder="— sin plan —" />
                </ShSelectTrigger>
                <ShSelectContent>
                  <ShSelectItem value="__none__">— sin plan —</ShSelectItem>
                  {plans.map((p) => (
                    <ShSelectItem key={p.id} value={p.id}>
                      {p.name} · ${p.base_fee_usd}/mes + ${p.unit_price_usd}/{p.unit_bundle_size} {p.unit_event_type}
                    </ShSelectItem>
                  ))}
                </ShSelectContent>
              </ShSelect>
              <p className="mt-3 text-[11px] text-slate-500">
                Gestione los planes en{" "}
                <a className="font-semibold text-accent-blue hover:underline" href="/admin/planes">/admin/planes →</a>{" "}
                y vea la facturación consolidada en{" "}
                <a className="font-semibold text-accent-blue hover:underline" href="/admin/facturacion">/admin/facturacion →</a>.
              </p>
            </div>
            <div className="border border-slate-200 bg-slate-50 p-4">
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Plan asignado</p>
              <p className="mt-2 font-display text-sm text-slate-900" data-testid="current-plan-name">
                {(() => {
                  const p = plans.find((x) => x.id === agent.plan_id);
                  return p ? p.name : "Sin plan";
                })()}
              </p>
              {planAssigning && (
                <p className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-500">
                  <Loader2 className="h-3 w-3 animate-spin" /> Aplicando…
                </p>
              )}
            </div>
          </div>
        </Section>

        {/* Webhook firmado */}
        <Section
          title="Webhook firmado (eventos reales)"
          hint="Conecte sistemas externos (CRM, ticketing, SAP, etc.) para reportar tareas completadas, fallidas o feedback. Los eventos firmados con HMAC-SHA256 alimentan las métricas reales del agente."
        >
          <div className="border border-slate-200 bg-white p-6" data-testid="webhook-section">
            {whLoading || !wh ? (
              <div className="flex items-center gap-3 text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Cargando webhook...
              </div>
            ) : (
              <div className="space-y-6">
                {/* URL */}
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 flex items-center gap-1.5">
                    <Webhook className="h-3 w-3" /> Endpoint público (POST)
                  </p>
                  <div className="mt-2 flex items-center gap-2 border border-slate-200 bg-slate-50 px-3 py-2">
                    <code className="flex-1 truncate font-mono text-[12px] text-slate-800" data-testid="webhook-url">
                      {wh.url}
                    </code>
                    <button
                      onClick={() => copy(wh.url, "url")}
                      className="text-slate-500 hover:text-slate-900"
                      title="Copiar"
                      data-testid="copy-webhook-url"
                    >
                      {copied === "url" ? <Check className="h-4 w-4 text-accent-blue" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Secret */}
                <div>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                      Secreto HMAC-SHA256
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowSecret((s) => !s)}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-700 hover:text-slate-900"
                        data-testid="toggle-secret-btn"
                      >
                        {showSecret ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        {showSecret ? "Ocultar" : "Mostrar"}
                      </button>
                      <button
                        onClick={onRotate}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-rose-600 hover:text-rose-700"
                        data-testid="rotate-secret-btn"
                      >
                        <RefreshCw className="h-3 w-3" /> Rotar
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2 border border-slate-200 bg-slate-50 px-3 py-2">
                    <code className="flex-1 truncate font-mono text-[12px] text-slate-800" data-testid="webhook-secret">
                      {showSecret ? wh.secret : wh.secret.replace(/./g, "•").slice(0, 32) + "..."}
                    </code>
                    <button
                      onClick={() => copy(wh.secret, "secret")}
                      className="text-slate-500 hover:text-slate-900"
                      title="Copiar secreto"
                      data-testid="copy-webhook-secret"
                    >
                      {copied === "secret" ? <Check className="h-4 w-4 text-accent-blue" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="mt-2 text-[11px] italic text-amber-700">
                    Nunca comparta este secreto fuera de su sistema. Para revocar el acceso, rote el secreto.
                  </p>
                </div>

                {/* Header + events */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Header esperado</p>
                    <code className="mt-2 block whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-slate-800 bg-slate-50 border border-slate-200 px-3 py-2">
                      {wh.header}
                    </code>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Eventos aceptados</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(wh.accepted_events || []).map((e) => (
                        <span
                          key={e}
                          className="border border-slate-300 px-2 py-0.5 font-mono text-[10px] text-slate-700"
                        >
                          {e}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* cURL example */}
                <div>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Ejemplo cURL</p>
                    <button
                      onClick={() => copy(wh.curl_example || "", "curl")}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-700 hover:text-slate-900"
                      data-testid="copy-webhook-curl"
                    >
                      {copied === "curl" ? <Check className="h-3 w-3 text-accent-blue" /> : <Copy className="h-3 w-3" />}
                      Copiar
                    </button>
                  </div>
                  <pre className="mt-2 max-h-[260px] overflow-auto border border-slate-200 bg-slate-950 px-4 py-3 font-mono text-[11px] leading-relaxed text-slate-100 thin-scroll">
{wh.curl_example}
                  </pre>
                </div>

                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                  Las métricas reales (tareas completadas, feedback) se actualizan automáticamente cada vez que llega un evento firmado.
                  Verifíquelas en{" "}
                  <a className="text-accent-blue font-semibold hover:underline" href={`/agentes/${slug}/metricas`}>el panel de métricas →</a>
                </p>
              </div>
            )}
          </div>
        </Section>

        {/* Footer save */}
        <div className="mt-12 flex items-center justify-between border-t border-slate-300 pt-6">
          <div className="text-xs text-slate-500">
            <Sparkles className="mr-2 inline h-3 w-3 text-accent-blue" />
            Cambios sin guardar se pierden al salir.
          </div>
          <Button
            onClick={onSave}
            disabled={saving}
            className="rounded-sm bg-slate-900 text-xs uppercase tracking-[0.2em] text-white hover:bg-slate-800"
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Guardar agente
          </Button>
        </div>
      </main>
    </div>
  );
}

function Section({ title, counter, hint, warning, children }) {
  return (
    <section className="mt-12">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="font-display text-2xl font-medium text-slate-900">{title}</h2>
        {counter && (
          <span className="rounded-sm border border-slate-300 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] text-slate-700">
            {counter}
          </span>
        )}
      </div>
      {warning && <p className="mt-2 text-xs italic text-amber-700">{warning}</p>}
      {hint && <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-600">{hint}</p>}
      <div className="mt-6">{children}</div>
    </section>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div className="mb-6">
      <label className="text-[10px] uppercase tracking-[0.22em] text-slate-700">
        {label} {required && <span className="text-rose-600">*</span>}
      </label>
      {hint && <p className="mt-1 max-w-2xl text-xs italic text-slate-500">{hint}</p>}
      <div className="mt-3">{children}</div>
    </div>
  );
}

function ExportCard({ href, testId, icon: Icon, title, desc, ext }) {
  return (
    <a
      href={href}
      download
      data-testid={testId}
      className="group flex flex-col border border-slate-200 bg-white p-5 transition hover:border-teal-600/60 hover:shadow-sm"
    >
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center bg-slate-900 text-accent-blue-light">
          <Icon className="h-4 w-4" strokeWidth={1.8} />
        </span>
        <div>
          <p className="font-display text-base font-semibold text-slate-900">{title}</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">{ext}</p>
        </div>
      </div>
      <p className="mt-4 text-sm leading-relaxed text-slate-600">{desc}</p>
      <span className="mt-5 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-accent-blue group-hover:text-teal-800">
        <Download className="h-3 w-3" /> Descargar
      </span>
    </a>
  );
}
